import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  fetchCredits,
  fetchDailyStepUsage,
  fetchMonthlyQuota
} from '../../api/billing';
import MonthlyQuotaOverview from '../../components/charts/MonthlyQuotaOverview';
import CreditsUsedChartCard from './CreditsUsedChartCard';
import { nowInPT } from '../../utils/BillingHelpers';

const UsagePage: React.FC = () => {
  const [creditsBalance, setCreditsBalance] = useState<number | null>(null);
  const [monthlyQuota, setMonthlyQuota] = useState<number | null>(null);
  const [currentMonthUsage, setCurrentMonthUsage] = useState<
    Array<{ date: string; credits: number }>
  >([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchQuotaData = async () => {
      setPageLoading(true);
      try {
        const current = nowInPT();
        const month1Based = current.getMonth() + 1;
        const year = current.getFullYear();

        const [creditsRes, quotaRes, usageRes] = await Promise.all([
          fetchCredits().catch(() => null),
          fetchMonthlyQuota().catch(() => null),
          fetchDailyStepUsage(month1Based, year)
        ]);

        setCreditsBalance(creditsRes?.balance ?? null);
        setMonthlyQuota(quotaRes ?? null);
        setCurrentMonthUsage(
          (usageRes?.daily_usage || []).map(({ date, steps }) => ({
            date: date.split('T')[0],
            credits: steps
          }))
        );
      } catch (err) {
        console.error('Error fetching quota data:', err);
        setCreditsBalance(null);
        setMonthlyQuota(null);
        setCurrentMonthUsage([]);
      } finally {
        setPageLoading(false);
      }
    };

    fetchQuotaData();
  }, []);

  const totalCreditsUsed = useMemo(() => {
    if (monthlyQuota != null && creditsBalance != null) {
      return monthlyQuota - creditsBalance;
    }
    return 0;
  }, [monthlyQuota, creditsBalance]);

  const usagePercent = monthlyQuota
    ? Math.min(100, Math.round((totalCreditsUsed / monthlyQuota) * 100))
    : 0;

  const getProgressColor = () => {
    if (usagePercent >= 90) return 'error';
    if (usagePercent >= 70) return 'warning';
    return 'success';
  };

  return (
    <Box
      sx={{
        padding: '24px',
        width: '100%',
        maxWidth: '900px',
        boxSizing: 'border-box'
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Usage
      </Typography>

      {pageLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={300}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MonthlyQuotaOverview
            stepsRemaining={creditsBalance}
            monthlyQuota={monthlyQuota}
            usagePercent={usagePercent}
            getProgressColor={getProgressColor}
          />
          <CreditsUsedChartCard />
        </Box>
      )}
    </Box>
  );
};

export default UsagePage;
